import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BranchDropdownMenu from '@/components/common/BranchDropdownMenu';
import LangDropdownMenu from '@/components/common/LangDropdownMenu';
import LoginUserDropdownMenu from '@/components/common/LoginUserDropdownMenu';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { getFileUrl } from '@/services/CommonService';
import { setAdminSideDrawer } from '@/store/actions';
import { updateAdminService } from '@/services/AdminServices';
import ToastMe from '@/components/ui/ToastMe';

const AdminHeader = ({
  title,
  faviconIcon, // optional override
  orderNotificationMessage,
  orderNotification,
  closeOrderNotificationModal,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { generalSetting, adminSideDrawer } = useSelector((s) => s.GeneralSetting);
  const admin = useSelector((s) => s.Auth?.data?.data);

  // App/Company name for <title>
  const appName = useMemo(() => {
    const fromSettings =
      generalSetting?.site_name ||
      generalSetting?.company_name ||
      generalSetting?.app_name ||
      null;
    const fromLS = (() => { try { return localStorage.getItem('company_name') || null; } catch { return null; } })();
    return fromSettings || fromLS || 'TabanFood';
  }, [generalSetting?.site_name, generalSetting?.company_name, generalSetting?.app_name]);

  // build a fresh cache-busted URL every time settings change
  const nowParam = () => `r=${Date.now()}`;

  // Header logo src: prefer *_url (already cache-busted by ThemePage), else file name with fresh buster
  const themeLogoSrc = useMemo(() => {
    if (generalSetting?.theme_logo_url) {
      const sep = generalSetting.theme_logo_url.includes('?') ? '&' : '?';
      return `${generalSetting.theme_logo_url}${sep}${nowParam()}`;
    }
    const fn = generalSetting?.theme_logo;
    if (fn) return `${getFileUrl(fn, 'theme')}?${nowParam()}`;
    return '/assets/images/theme/theme-logo.png';
  }, [generalSetting?.theme_logo_url, generalSetting?.theme_logo]);

  // Favicon src: fav_icon_url → fav_icon → theme_logo_url → theme_logo → /favicon.ico
  const faviconSrc = useMemo(() => {
    if (faviconIcon) return faviconIcon; // explicit override
    const url = generalSetting?.fav_icon_url || generalSetting?.theme_logo_url || null;
    if (url) {
      const sep = url.includes('?') ? '&' : '?';
      return `${url}${sep}${nowParam()}`;
    }
    const fav = generalSetting?.fav_icon;
    if (fav) return `${getFileUrl(fav, 'theme')}?${nowParam()}`;
    const logo = generalSetting?.theme_logo;
    if (logo) return `${getFileUrl(logo, 'theme')}?${nowParam()}`;
    return '/favicon.ico';
  }, [faviconIcon, generalSetting?.fav_icon_url, generalSetting?.theme_logo_url, generalSetting?.fav_icon, generalSetting?.theme_logo]);

  const handleLinkClick = (e) => {
    e.preventDefault();
    closeOrderNotificationModal?.();
    if (orderNotification?.url) navigate(`/admin/${orderNotification.url}`);
  };

  const handleDrawer = () => {
    dispatch(setAdminSideDrawer(!adminSideDrawer));
    document.body.style.overflowY = adminSideDrawer ? 'auto' : 'hidden';
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !admin?._id) return ToastMe('No admin found', 'error');
    const formData = new FormData();
    formData.append('photo', file);
    try {
      await dispatch(updateAdminService(admin._id, formData));
      ToastMe('Profile photo updated', 'success');
    } catch (err) {
      ToastMe(err?.message || 'Failed to upload photo', 'error');
    }
  };

  return (
    <>
      <Helmet>
        <title>{title && title !== '' ? `${title} • ${appName}` : appName}</title>
        <link rel="icon" href={faviconSrc} />
      </Helmet>

      <header className="db-header">
        {/* Brand / Logo */}
        <Link
          to="/admin/dashboard"
          className="flex-shrink-0 px-3 py-2 w-56 flex items-center justify-center"
        >
          <img
            key={themeLogoSrc}
            className="max-h-12 w-auto object-contain"
            src={themeLogoSrc}
            alt="logo"
            onError={(e) => { e.currentTarget.src = '/assets/images/theme/theme-logo.png'; }}
          />
        </Link>

        {/* Right Controls */}
        <div className="flex items-center justify-end w-full gap-4">
          <div className="sub-header flex items-center gap-4 transition xh:justify-between xh:fixed xh:left-0 xh:w-full xh:p-4 xh:border-y xh:border-[#EFF0F6] xh:bg-white">
            <div className="flex items-center justify-between md:justify-center gap-4">
              <Link to="/admin/pos" className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#FFEBD8]" title="Open POS">
                <i className="lab lab-pos-bold lab-font-size-16 font-fill-pos"></i>
              </Link>
              <Link to="/admin/pos-orders" className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#E3F2FD]" title="POS Orders">
                <i className="lab lab-pos-orders lab-font-size-16 text-blue-600"></i>
              </Link>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <BranchDropdownMenu showAllOption />
            <LangDropdownMenu />
          </div>

          <button
            type="button"
            onClick={handleDrawer}
            className="fa-solid fa-align-left db-header-nav w-9 h-9 rounded-lg text-primary bg-primary/5"
            title="Toggle sidebar"
          />

          <LoginUserDropdownMenu onPhotoChange={handlePhotoChange} />
        </div>
      </header>

      {/* Order notification modal */}
      <div id="order" className="modal ff-modal">
        <div className="modal-dialog max-w-[360px] p-6 text-center relative">
          <button onClick={closeOrderNotificationModal} className="modal-close absolute top-4 right-4">
            <i className="fa-regular fa-circle-xmark"></i>
          </button>
          <h3 className="text-[18px] font-semibold leading-8 mb-6">
            {orderNotificationMessage}
            <span className="block">Please check your order list.</span>
          </h3>
          <button
            onClick={handleLinkClick}
            className="db-btn h-[38px] shadow-[0px_6px_10px_rgba(23,_114,_255,_0.24)] bg-primary text-white"
          >
            Let me check
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminHeader;
